// noinspection SpellCheckingInspection

import { Div, Icon, Input } from '@pinpoint/design-kit';
import adler32 from 'adler-32';
import { add, diff, divide, dotDivide, dotMultiply, multiply, subtract, sum } from 'mathjs';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import Button from 'src/components/button';
import { all, calcEqPersonsAdj, calcEqPremiumsAdj, formatNumberV2, formatPercent } from 'src/functions';
import usePlatformConfig from 'src/hooks/usePlatformConfig';
import {
  calculateYearlyDroppedPolicies,
  calculateYearlyValues,
  correctMeanPremiumPerPolicy,
  createPoliciesInForce,
} from 'src/functions/actuarial_calculator';
import { ModelProp, RoiCalcSeedProp, RoiCalcStateProp } from 'src/store/typings';

import { CellRegulatoryTag, CellTag } from '../../cells';
import Legend from './legend';
import YearImpact from './year_impact';

/** @type YearDetail */
const defaultYearInfo = {
  loss: new Array(10).fill(0),
  lr: new Array(10).fill(0),
  policies: new Array(10).fill(0),
  premium: new Array(10).fill(0),
  sumLoss: 0,
  sumPolicies: 0,
  sumPremium: 0,
  totalLR: 0,
};

/**
 * @type {number[]}
 */
const DECILES = Array.from(Array(10), (e, i) => i + 1);

export default function CompareRoiCalculator({
  models,
  seeds,
  equalType,
  calcState: { yearlyRates, nonRenewalDistribution, acquisitionDistribution, policiesInForce },
  resetRoiCalcStateField,
  updateRoiCalcState,
}) {
  const platformConfig = usePlatformConfig();
  const goodAcqD = sum(acquisitionDistribution) === 100;
  const goodNrD = sum(nonRenewalDistribution) === 100;
  const inGroupCmp = models.every((mdl) => mdl?.modelGroupId === models[0]?.modelGroupId);
  const processDistInput = useCallback(
    (evt) => {
      evt.preventDefault();
      const { target } = evt;
      const { id, value: trial } = target;
      /** @type {string} */
      const validOnly = trial.replace(/\D+/g, '');
      updateRoiCalcState(id, +validOnly);
      const position = `${+validOnly}`.length;
      setTimeout(() => {
        target.setSelectionRange(position, position);
      }, 25);
    },
    [updateRoiCalcState]
  );

  const processPercentageInput = useCallback(
    (evt) => {
      evt.preventDefault();
      const { target } = evt;
      const { id, value: trial } = target;
      const validOnly = +trial.replace(/\D+/g, '');
      if (![null, undefined].includes(validOnly)) updateRoiCalcState(id, validOnly > 100 ? 100 : validOnly);
      else updateRoiCalcState(id, 0);
      const position = `${validOnly}`.length;
      setTimeout(() => target.setSelectionRange(position, position), 25);
    },
    [updateRoiCalcState]
  );

  const processIntegerInput = useCallback(
    (evt) => {
      evt.preventDefault();
      const { target } = evt;
      const { value: trial } = target;
      const validOnly = +trial.replace(/\D+/g, '');
      updateRoiCalcState('policiesInForce', validOnly);
    },
    [updateRoiCalcState]
  );
  const [scratch, setScratch] = useState([
    {
      wP: {
        y1: defaultYearInfo,
        y2: defaultYearInfo,
        y3: defaultYearInfo,
      },
      ss: {
        y1: defaultYearInfo,
        y2: defaultYearInfo,
        y3: defaultYearInfo,
      },
      crnt: defaultYearInfo,
    },
    {
      wP: {
        y1: defaultYearInfo,
        y2: defaultYearInfo,
        y3: defaultYearInfo,
      },
      ss: {
        y1: defaultYearInfo,
        y2: defaultYearInfo,
        y3: defaultYearInfo,
      },
      crnt: defaultYearInfo,
    },
    {
      wP: {
        y1: defaultYearInfo,
        y2: defaultYearInfo,
        y3: defaultYearInfo,
      },
      ss: {
        y1: defaultYearInfo,
        y2: defaultYearInfo,
        y3: defaultYearInfo,
      },
      crnt: defaultYearInfo,
    },
  ]);
  const [calculate, setCalculate] = useState(true);

  useEffect(() => {
    if (calculate && models?.length && seeds?.length) {
      const internalScratchPromise = models?.map?.(async (model, idx) => {
        const mdl = {};
        /** @type {YearDetail} */
        const crnt = {};
        const cncl = {
          y1: {},
          y2: {},
          y3: {},
        };
        /**
         * @property {YearDetail} y1
         * @property {YearDetail} y2
         * @property {YearDetail} y3
         */
        const ss = {
          y1: {},
          y2: {},
          y3: {},
        };
        /**
         * @property {YearDetail} y1
         * @property {YearDetail} y2
         * @property {YearDetail} y3
         */
        const wP = {
          y1: {},
          y2: {},
          y3: {},
        };
        const isLegacy = ['LIFT_VERSION_1_ACT', null, undefined].includes(model?.payload?.version);
        const isPersonalAuto = model?.typeDetail.includes('PERSONAL AUTO');
        const seed = seeds[idx];
        let liftScore = [];
        if (isLegacy)
          liftScore = model?.modelData?.dataPayload?.lift_scores?.find?.((info) => info?.p_lift?.length === 10) ?? [];
        if (equalType === 'premiums')
          liftScore =
            (
              model?.modelData?.dataPayload?.equal_premiums?.lift_scores ??
              model?.modelData?.dataPayload?.equal_premium?.lift_scores
            )?.find?.((info) => info?.p_lift?.length === 10) ?? [];
        if (equalType === 'persons')
          liftScore =
            model?.modelData?.dataPayload?.equal_persons?.lift_scores?.find?.((info) => info?.p_lift?.length === 10) ??
            [];
        if (!liftScore?.p_pif_percentile)
          return {
            mdl,
            crnt,
            ss,
            wP,
          };
        /** @type {math.Unit} */
        const newBDR = divide(acquisitionDistribution, 100);
        const [[y1nr, y1nb], [y2nr, y2nb], [y3nr, y3nb]] = divide(yearlyRates, 100);
        // adjusted values by pif percentile
        const { pinpointLosses, pinpointPremiums } =
          equalType === 'persons'
            ? calcEqPersonsAdj(
                liftScore?.p_pif_percentile ?? [],
                liftScore?.p_risk_cost ?? [],
                liftScore?.p_premium ?? []
              )
            : calcEqPremiumsAdj(
                liftScore?.p_pif_percentile ?? new Array(10).fill(0),
                liftScore?.p_risk_cost ?? [],
                liftScore?.p_premium ?? []
              );

        // Current and Model Data
        crnt.policies = createPoliciesInForce(inGroupCmp ? policiesInForce : seed?.policies_in_force, isPersonalAuto);
        crnt.sumPolicies = sum(crnt.policies);

        mdl.loss = pinpointLosses;
        mdl.sumLoss = sum(mdl.loss);
        mdl.premium = pinpointPremiums;
        mdl.sumPremium = sum(mdl.premium);
        mdl.avgPremium = correctMeanPremiumPerPolicy(liftScore?.avg_premium, seed?.avg_yearly_premium, isPersonalAuto);
        mdl.sumAvgPremium = sum(mdl.avgPremium);

        crnt.premium = dotMultiply(crnt.policies, mdl.avgPremium);
        crnt.sumPremium = sum(crnt.premium);
        crnt.lr = dotDivide(mdl.loss, mdl.premium);
        crnt.totalLR = mdl.sumLoss / mdl.sumPremium;

        // Year 1 - Steady State
        ss.y1 = calculateYearlyValues(
          multiply(crnt.policies, 1 + Math.abs(diff(yearlyRates[0])) / 100),
          crnt,
          mdl.avgPremium
        );

        // Year 2 - Steady State
        ss.y2 = calculateYearlyValues(
          multiply(ss.y1.policies, 1 + Math.abs(diff(yearlyRates[1])) / 100),
          ss.y1,
          mdl.avgPremium
        );

        // Year 3 - Steady State
        ss.y3 = calculateYearlyValues(
          multiply(ss.y2.policies, 1 + Math.abs(diff(yearlyRates[2])) / 100),
          ss.y2,
          mdl.avgPremium
        );

        // Year 1 - With Pinpoint
        cncl.y1 = calculateYearlyDroppedPolicies(y1nr, crnt, nonRenewalDistribution);
        wP.y1 = calculateYearlyValues(
          subtract(add(multiply(newBDR, y1nb, crnt.sumPolicies), crnt.policies), cncl.y1.finalDrop),
          crnt,
          mdl.avgPremium
        );

        // Year 2 - With Pinpoint
        cncl.y2 = calculateYearlyDroppedPolicies(y2nr, wP.y1, nonRenewalDistribution);
        wP.y2 = calculateYearlyValues(
          subtract(add(multiply(newBDR, y2nb, wP.y1.sumPolicies), wP.y1.policies), cncl.y2.finalDrop),
          wP.y1,
          mdl.avgPremium
        );

        // Year 3 - With Pinpoint
        cncl.y3 = calculateYearlyDroppedPolicies(y3nr, wP.y2, nonRenewalDistribution);
        wP.y3 = calculateYearlyValues(
          subtract(add(multiply(newBDR, y3nb, wP.y2.sumPolicies), wP.y2.policies), cncl.y3.finalDrop),
          wP.y2,
          mdl.avgPremium
        );
        return {
          mdl,
          crnt,
          ss,
          wP,
        };
      });
      Promise.all(internalScratchPromise).then(setScratch);
      setCalculate(false);
    }
  }, [
    acquisitionDistribution,
    calculate,
    equalType,
    inGroupCmp,
    models,
    nonRenewalDistribution,
    policiesInForce,
    seeds,
    yearlyRates,
  ]);

  useEffect(() => {
    if (seeds?.length && inGroupCmp) updateRoiCalcState('policiesInForce', +seeds[0].policies_in_force);
  }, [seeds, inGroupCmp, updateRoiCalcState]);

  return (
    <Div
      d="grid"
      h="100%"
      w="100%"
      maxH="100%"
      maxW="100%"
      minH="100%"
      minW="100%"
      p="1rem"
      gridTplC={inGroupCmp ? '1fr calc(1rem/2) 3fr' : 'ifr'}
      gridTplR="1fr 1rem 1fr 1rem 1fr 1rem repeat(2, .5fr)"
      gridGap="0"
      textSize="caption"
      textColor="shade4D"
    >
      {inGroupCmp && (
        <Div
          gridArea="1 / 1 / -1 / 2"
          gridTplR=".1fr 1fr"
          gridGap="calc(1rem/4)"
          gridTplC="1fr"
          d="grid"
          textWeight="500"
          data-debug="left"
          textColor="black"
        >
          <Legend orient="h" />
          <Div
            d="grid"
            gridArea="2 / 1 / 3 / -1"
            gridTplC="repeat(2, .4fr) repeat(3, .5fr)"
            gridTplR="2rem 1.25rem calc(1rem/2) repeat(2, 2rem) repeat(2, 1.25rem) calc(1rem/2) 2rem auto repeat(11, 1.25rem) 3rem 1fr"
            gridGap="calc(1rem/16)"
            data-debug="inputs"
            textColor="shade4D"
          >
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="1 / 1 / 1 / -1"
              bg="shadeEF"
              rounded="sm"
              textSize="body"
            >
              INPUTS
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-end"
              p={{ r: '.5rem' }}
              textWeight="500"
              gridArea="2 / 1 / 3 / 3"
              bg="rioGrande"
              rounded="sm"
              whiteSpace="nowrap"
            >
              Policies in Force:
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              p={{ l: '.5rem', r: '.25rem' }}
              textWeight={undefined}
              gridArea="2 / 3 / 3 / -1"
              bg="marigoldPale"
              rounded="sm"
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="pif-input"
                placeholder={formatNumberV2(100000)}
                name="pif-input"
                onChange={processIntegerInput}
                value={policiesInForce ? formatNumberV2(policiesInForce, true, true) : ''}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="4 / 1 / 5 / -1"
              bg="shadeEF"
              rounded="sm"
              textSize="body"
            >
              PINPOINT UW SELECTION INPUTS
            </Div>
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="5 / 1 / 6 / 3"
              bg="shadeEF"
              rounded="sm"
            />
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="5 / 3 / 6 / 4"
              bg="shadeEF"
              rounded="sm"
            >
              YEAR 1
            </Div>
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="5 / 4 / 6 / 5"
              bg="shadeEF"
              rounded="sm"
            >
              YEAR 2
            </Div>
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="5 / 5 / 6 / 6"
              bg="shadeEF"
              rounded="sm"
            >
              YEAR 3
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-end"
              p={{ r: '.5rem' }}
              textWeight="500"
              gridArea="6 / 1 / 7 / 3"
              bg="rioGrande"
              rounded="sm"
              whiteSpace="nowrap"
            >
              Non-Renewal Rate*:
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              textWeight={undefined}
              gridArea="6 / 3 / 7 / 4"
              bg="marigoldPale"
              rounded="sm"
              p={{ x: '.25rem' }}
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="yearlyRates.0.0"
                placeholder={`${yearlyRates[0][0]}%`}
                name="yearlyRates.0.0"
                maxLength={4}
                onChange={processPercentageInput}
                onClick={(evt) => {
                  const { target } = evt;
                  const { value } = target;
                  target.setSelectionRange(value.length - 1, value.length - 1);
                }}
                value={`${yearlyRates[0][0]}%`}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              p={{ x: '.25rem' }}
              textWeight={undefined}
              gridArea="6 / 4 / 7 / 5"
              bg="marigoldPale"
              rounded="sm"
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="yearlyRates.1.0"
                placeholder={`${yearlyRates[1][0]}%`}
                name="yearlyRates.1.0"
                maxLength={4}
                onChange={processPercentageInput}
                onClick={(evt) => {
                  const { target } = evt;
                  const { value } = target;
                  target.setSelectionRange(value.length - 1, value.length - 1);
                }}
                value={`${yearlyRates[1][0]}%`}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              p={{ x: '.25rem' }}
              textWeight={undefined}
              gridArea="6 / 5 / 7 / 6"
              bg="marigoldPale"
              rounded="sm"
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="yearlyRates.2.0"
                placeholder={`${yearlyRates[2][0]}%`}
                name="yearlyRates.2.0"
                maxLength={4}
                onChange={processPercentageInput}
                onClick={(evt) => {
                  const { target } = evt;
                  const { value } = target;
                  target.setSelectionRange(value.length - 1, value.length - 1);
                }}
                value={`${yearlyRates[2][0]}%`}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-end"
              p={{ r: '.5rem' }}
              textWeight="500"
              gridArea="7 / 1 / 8 / 3"
              bg="rioGrande"
              rounded="sm"
              whiteSpace="nowrap"
            >
              New Business Rate**:
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              textWeight={undefined}
              gridArea="7 / 3 / 8 / 4"
              bg="marigoldPale"
              rounded="sm"
              p={{ x: '.25rem' }}
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="yearlyRates.0.1"
                placeholder={`${yearlyRates[0][1]}%`}
                name="yearlyRates.0.1"
                maxLength={4}
                onChange={processPercentageInput}
                onClick={(evt) => {
                  const { target } = evt;
                  const { value } = target;
                  target.setSelectionRange(value.length - 1, value.length - 1);
                }}
                value={`${yearlyRates[0][1]}%`}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              p={{ x: '.25rem' }}
              textWeight={undefined}
              gridArea="7 / 4 / 8 / 5"
              bg="marigoldPale"
              rounded="sm"
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="yearlyRates.1.1"
                placeholder={`${yearlyRates[1][1]}%`}
                name="yearlyRates.1.1"
                maxLength={4}
                onChange={processPercentageInput}
                onClick={(evt) => {
                  const { target } = evt;
                  const { value } = target;
                  target.setSelectionRange(value.length - 1, value.length - 1);
                }}
                value={`${yearlyRates[1][1]}%`}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="flex-start"
              p={{ x: '.25rem' }}
              textWeight={undefined}
              gridArea="7 / 5 / 8 / 6"
              bg="marigoldPale"
              rounded="sm"
            >
              <Input
                textColor="shade4D"
                textAlign="right"
                type="text"
                id="yearlyRates.2.1"
                placeholder={`${yearlyRates[2][1]}%`}
                name="y3-nbr"
                maxLength={4}
                onChange={processPercentageInput}
                onClick={(evt) => {
                  const { target } = evt;
                  const { value } = target;
                  target.setSelectionRange(value.length - 1, value.length - 1);
                }}
                value={`${yearlyRates[2][1]}%`}
                borderColor="shadeD9"
                border="1px solid"
                maxH="1rem"
                h="1rem"
                m="0"
                p={{ x: '.25rem' }}
                rounded="sm"
                textSize="small"
              />
            </Div>
            <Div
              d="flex"
              align="center"
              justify="center"
              textWeight="500"
              gridArea="9 / 1 / 10 / -1"
              bg="shadeEF"
              rounded="sm"
              textSize="body"
            >
              UW DISTRIBUTION SELECTIONS***
            </Div>
            <Div
              d="grid"
              gridArea="10 / 1 / -2 /-1"
              align="stretch"
              justify="center"
              textWeight="500"
              gridTplR="auto repeat(11, 1.25rem) 2rem"
              gridTplC="repeat(3, 1fr)"
              gridGap="calc(1rem/16)"
            >
              <Div
                d="flex"
                align="center"
                justify="flex-end"
                p={{ r: '.5rem' }}
                textWeight="500"
                gridArea="1 / 1 / 2 / 2"
                bg="rioGrande"
                rounded="sm"
                whiteSpace="nowrap"
              >
                Prediction Decile
              </Div>
              {DECILES.map((val, idx) => (
                <Div
                  key={`decile-${val}`}
                  d="flex"
                  align="center"
                  justify="right"
                  textAlign="right"
                  p={{ x: '.75rem' }}
                  gridArea={`${2 + idx} / 1 / ${3 + idx} / 2`}
                  bg="marigoldPale"
                  rounded="sm"
                >
                  {idx + 1}
                </Div>
              ))}
              <Div
                d="flex"
                align="center"
                justify="right"
                textAlign="right"
                textWeight="700"
                textColor="black"
                p={{ x: '.75rem' }}
                gridArea="-3 / 1 / -2 / 2"
                bg="marigoldPale"
                rounded="sm"
              >
                TOTAL
              </Div>
              <Div
                d="flex"
                align="center"
                justify="center"
                textWeight="500"
                gridArea="1 / 2 / 2 / 3"
                bg="shadeEF"
                rounded="sm"
                p={{ x: '.5rem' }}
              >
                Pinpoint New Business
                <br />
                Distribution
              </Div>
              {acquisitionDistribution.map((val, idx) => (
                <Div
                  key={`ppt-nb-dist-${adler32.str(idx.toString(), idx)}`}
                  d="flex"
                  align="center"
                  justify="right"
                  textAlign="right"
                  p={{ x: '.5rem' }}
                  gridArea={`${2 + idx} / 2 / ${3 + idx} / 3`}
                  bg="marigoldPale"
                  rounded="sm"
                >
                  <Input
                    textColor="shade4D"
                    textAlign="right"
                    type="text"
                    id={`acquisitionDistribution.${idx}`}
                    placeholder={`${val}%`}
                    name={`acquisitionDistribution.${idx}`}
                    maxLength={4}
                    onChange={processDistInput}
                    onClick={(evt) => {
                      const { target } = evt;
                      const { value } = target;
                      target.setSelectionRange(value.length - 1, value.length - 1);
                    }}
                    value={`${val}%`}
                    borderColor="shadeD9"
                    border="1px solid"
                    maxH="1rem"
                    h="1rem"
                    m="0"
                    p={{ x: '.25rem' }}
                    rounded="sm"
                    textSize="small"
                  />
                </Div>
              ))}
              <Div
                data-test="ppt-nb-dist-sum"
                d="flex"
                align="center"
                justify="right"
                textAlign="right"
                p={{ x: '.5rem' }}
                textWeight="700"
                gridArea="-3 / 2 / -2 / 3"
                bg="marigoldPale"
                rounded="sm"
                textColor={goodAcqD ? 'shade4D' : 'danger800'}
                whiteSpace="nowrap"
              >
                <Button
                  m={{ r: '0.25rem' }}
                  p={{ x: '0.25rem' }}
                  h="1rem"
                  bg="rioGrande"
                  textSize="tiny"
                  textWeight="700"
                  onClick={() => resetRoiCalcStateField('acquisitionDistribution')}
                >
                  <Icon name="Reload" size="0.625rem" viewBox="0 0 12 11.75" color="white" m={{ r: '.25rem' }} />
                  DEFAULT
                </Button>
                {sum(acquisitionDistribution)}%
              </Div>
              <Div
                d="flex"
                align="center"
                justify="center"
                textWeight="500"
                gridArea="1 / 3 / 2 / 4"
                bg="shadeEF"
                rounded="sm"
                p={{ x: '.5rem' }}
              >
                Pinpoint Non-Renewal
                <br />
                Distribution
              </Div>
              {nonRenewalDistribution.map((val, idx) => (
                <Div
                  key={`ppt-nr-dist-${adler32.str(idx.toString(), idx)}`}
                  d="flex"
                  align="center"
                  justify="right"
                  textAlign="right"
                  p={{ x: '.5rem' }}
                  gridArea={`${2 + idx} / 3 / ${3 + idx} / 4`}
                  bg="marigoldPale"
                  rounded="sm"
                >
                  <Input
                    textColor="shade4D"
                    textAlign="right"
                    type="text"
                    id={`nonRenewalDistribution.${idx}`}
                    placeholder={`${val}%`}
                    name={`nonRenewalDistribution.${idx}`}
                    maxLength={4}
                    onChange={processDistInput}
                    onClick={(evt) => {
                      const { target } = evt;
                      const { value } = target;
                      target.setSelectionRange(value.length - 1, value.length - 1);
                    }}
                    value={`${val}%`}
                    borderColor="shadeD9"
                    border="1px solid"
                    maxH="1rem"
                    h="1rem"
                    m="0"
                    p={{ x: '.25rem' }}
                    rounded="sm"
                    textSize="small"
                  />
                </Div>
              ))}
              <Div
                data-test="ppt-nr-dist-sum"
                d="flex"
                align="center"
                justify="right"
                textAlign="right"
                p={{ x: '.5rem' }}
                gridArea="-3 / 3 / -2 / 4"
                bg="marigoldPale"
                rounded="sm"
                textWeight="700"
                textColor={goodNrD ? 'shade4D' : 'danger800'}
                whiteSpace="nowrap"
              >
                <Button
                  m={{ r: '0.25rem' }}
                  p={{ x: '0.25rem' }}
                  h="1rem"
                  bg="rioGrande"
                  textSize="tiny"
                  textWeight="700"
                  onClick={() => resetRoiCalcStateField('nonRenewalDistribution')}
                >
                  <Icon name="Reload" size="0.625rem" viewBox="0 0 12 11.75" color="white" m={{ r: '.25rem' }} />
                  DEFAULT
                </Button>
                {sum(nonRenewalDistribution)}%
              </Div>
              <Div
                d="flex"
                align="center"
                justify="center"
                textWeight="500"
                gridArea="-2 / 1 / -1 / 2"
                bg="shadeEF"
                rounded="sm"
              />
              <Div
                data-test="calc-btn-div"
                d="flex"
                align="center"
                justify="center"
                gridArea="-2 / 2 / -1 / -1"
                bg="marigoldPale"
                rounded="sm"
              >
                <Button
                  m="0px"
                  onClick={() => setCalculate(true)}
                  buttonStyle="primary"
                  h="1.5rem"
                  textSize="small"
                  disabled={!all([goodAcqD, goodNrD])}
                >
                  <Icon name="Roi" size="0.625rem" viewBox="0 0 16 16" color="white" m={{ r: '.25rem' }} />
                  CALCULATE
                </Button>
              </Div>
            </Div>
          </Div>
        </Div>
      )}
      <Div
        gridArea={inGroupCmp ? '1 / 3 / -1 / -1' : '1 / 1 / -1 / -1'}
        gridTplR=".3fr .3fr .3fr 1fr"
        gridGap="calc(1rem)"
        gridTplC="1fr"
        d="grid"
        textWeight="500"
        data-debug="right"
        textSize="body"
        textColor="shade33"
      >
        {scratch?.map?.((scr, idx) => (
          <Div
            key={models?.[idx]?.name}
            gridArea={`${idx + 1} / 1 / ${idx + 2} / -1`}
            d="grid"
            gridTplC="repeat(2, 1fr) calc(calc(1rem/4) - 2px) repeat(3, 1fr) calc(calc(1rem/4) - 2px) repeat(3, 1fr) calc(calc(1rem/4) - 2px) repeat(3, 1fr) calc(calc(1rem/4) - 2px) 1fr"
            gridTplR="repeat(2, 2rem) 3rem repeat(2, 2rem) 1fr"
            gridGap="calc(1rem/16)"
            textWeight="700"
            data-debug={`summary-${models?.[idx]?.id}`}
          >
            <Div
              bg="shadeEF"
              rounded="sm"
              gridArea="1 / 1 / 4 / 3"
              d="flex"
              align="flex-end"
              justify="right"
              p={{ r: '.5rem', b: '.5rem' }}
            >
              {!inGroupCmp && `Initial PIF: ${formatNumberV2(seeds?.[idx]?.policies_in_force, true, true)}`}
            </Div>
            <Div
              d="flex"
              gridArea="4 / 1 / 5 / 3"
              align="center"
              justify="flex-end"
              p={{ r: '.5rem' }}
              bg="shadeEF"
              rounded="sm"
            >
              Loss Ratio
            </Div>
            <Div
              d="flex"
              gridArea="5 / 1 / 6 / 3"
              align="center"
              justify="flex-end"
              p={{ r: '.5rem' }}
              bg="shadeEF"
              rounded="sm"
            >
              Premium - (Loss + LAE)
            </Div>
            <Div d="flex" align="center" justify="center" gridArea="1 / 4 / 2 / -1" bg="shadeEF" rounded="sm">
              PINPOINT IMPACT SUMMARY&emsp;
              {CellTag({
                value: {
                  ...models?.[idx],
                  isRemediated: models?.[idx]?.modelData?.metadataPayload?.fairness ?? false,
                  isLegacy: platformConfig.isLegacyPlatform(
                    models?.[idx]?.modelData?.metadataPayload?.refresh?.platform
                  ),
                },
                bg: 'white',
              })}
              {models?.[idx]?.framework &&
                CellRegulatoryTag({ value: { framework: models?.[idx]?.framework }, bg: 'white', m: { l: '.25rem' } })}
              &emsp;
              {models?.[idx]?.name?.split?.(': ')?.[1]}
            </Div>
            <YearImpact year={1} crnt={scr.crnt} ss={scr.ss.y1} wP={scr.wP.y1} />
            <YearImpact year={2} crnt={scr.crnt} ss={scr.ss.y2} wP={scr.wP.y2} />
            <YearImpact year={3} crnt={scr.crnt} ss={scr.ss.y3} wP={scr.wP.y3} />
            <Div
              d="flex"
              align="center"
              justify="center"
              gridArea="2 / 16 / 3 / -1"
              gridTplC="repeat(3, 1fr)"
              gridTplR="2rem 3rem repeat(2, 2rem) 1fr"
              gridGap="calc(1rem/16)"
              bg="shadeEF"
              rounded="sm"
            >
              TOTAL
            </Div>
            <Div
              d="flex"
              p={{ r: '.75rem' }}
              align="center"
              justify="right"
              gridArea="3 / 16 / 4 / -1"
              bg="tealLight"
              rounded="sm"
              textColor="white"
              textAlign="right"
            >
              Expected
              <br />
              Impact
            </Div>
            <Div
              d="flex"
              p={{ r: '.75rem' }}
              align="center"
              justify="right"
              gridArea="4 / 16 / 5 / -1"
              bg="greenDarkPale2"
              rounded="sm"
              textAlign="right"
              whiteSpace="nowrap"
            >
              {formatPercent(
                Number.isFinite(scr.wP.y3.totalLR - scr.crnt.totalLR)
                  ? (scr.wP.y3.totalLR - scr.crnt.totalLR) * 100
                  : 0,
                false
              )}
            </Div>
            <Div
              d="flex"
              p={{ r: '.75rem' }}
              align="center"
              justify="right"
              gridArea="5 / 16 / 6 / -1"
              bg="greenDarkPale2"
              rounded="sm"
              textAlign="right"
              whiteSpace="nowrap"
            >
              $
              {formatNumberV2(
                ['y1', 'y2', 'y3'].reduce((acc, key) => acc + scr.wP[key].sumNet - scr.ss[key].sumNet, 0),
                true,
                true
              ) ?? 0}
            </Div>
          </Div>
        )) ?? []}
      </Div>
      {inGroupCmp && (
        <Div gridArea="-2 / 1 / -1 / -1" p={{ t: '1rem' }} textColor="shade4D" textWeight="500" textSize="small">
          <br />
          <Div textColor="brand500" textSize="body" textWeight="700">
            ABOUT
          </Div>
          <Div p={{ t: '.125rem' }}>
            This Impact Calculator shows how implementing a Pinpoint model via non-renewal and new-business strategies
            impacts the distribution of policies and profitability over time.
          </Div>
          <Div p={{ t: '.125rem' }}>
            * The Non-Renewal Rate represents the percent of in-force policies that the company is willing to triage
            with the selected Pinpoint Model.
          </Div>
          <Div p={{ t: '.125rem' }}>
            ** The New Business Rate represents the number of new-business policies the company is willing to target
            based on the Pinpoint Model, as a percent of the selected policies in force.
          </Div>
          <Div p={{ t: '.125rem' }}>
            *** The UW Distribution table allows you to make reasonable selections that allocate the non-renewed and
            new-business policies to their respective deciles based on the UW Selection Inputs above.
          </Div>
        </Div>
      )}
    </Div>
  );
}
CompareRoiCalculator.propTypes = {
  calcState: RoiCalcStateProp.isRequired,
  equalType: PropTypes.string.isRequired,
  models: PropTypes.arrayOf(ModelProp).isRequired,
  seeds: PropTypes.arrayOf(RoiCalcSeedProp).isRequired,
  updateRoiCalcState: PropTypes.func.isRequired,
  resetRoiCalcStateField: PropTypes.func.isRequired,
};
